import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Form,
  FormFeedback,
  Input,
  Label,
  Row,
  Spinner,
} from "reactstrap";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useTranslation } from "react-i18next";
import { toast, ToastContainer } from "react-toastify";
import BreadCrumb from "../../../../Components/Common/BreadCrumb";
import ImageCropModal from "../../../../Components/Common/ImageCropModal";
import PageHelpLink from "../../../../Components/Common/PageHelpLink";
import { useUnsavedChanges } from "../../../../contexts/UnsavedChangesContext";
import {
  getLoggedinUser,
  mergeTenantMeIntoSession,
} from "../../../../helpers/api_helper";
import {
  getInactiveContactsSummary,
  updateInactiveListSettings,
} from "../../../../helpers/audience_helper";
import {
  ACCOUNT_TIMEZONE_OPTIONS,
  deleteBrandLogo,
  deleteUserAvatar,
  fetchMe,
  updateProfile,
  uploadBrandLogo,
  uploadUserAvatar,
  type UpdateProfilePayload,
} from "../../../../helpers/profile_helper";
import AccountMfaSection from "./AccountMfaSection";
import AccountGoogleSection from "./AccountGoogleSection";
import { Navigate, useSearchParams } from "react-router-dom";
import {
  fetchBrandKit,
  updateBrandKit,
  type BrandKit,
} from "../../../../helpers/brand_kit_helper";
import {
  DEFAULT_TENANT_TIMEZONE,
  tenantTimezoneDisplayLabel,
} from "../../../../helpers/tenantTimezone";
import {
  PINNABLE_SHELL_ITEMS,
  isFavoriteId,
  readShellFavorites,
  writeShellFavorites,
  type ShellFavorite,
} from "../../../../helpers/shellFavorites";
import { helpUrlForRoute } from "../../../../constants/helpRouteMap";

const LOCALES = ["pt-BR", "en", "es"] as const;

type AccountFormValues = {
  name: string;
  email: string;
  current_password: string;
  password: string;
  password_confirmation: string;
  locale: string;
  timezone: string;
  /** Vazio = herda fuso da conta (users.timezone null). */
  user_timezone: string;
};

const emptySecrets = {
  current_password: "",
  password: "",
  password_confirmation: "",
};

/**
 * Conta do usuário tenant — nome, e-mail, senha, idioma, fuso e logo da marca (descadastro).
 * Por quê: Carla edita o próprio perfil sem SA; logo só na página pública de unsub (não WL do painel).
 */
const AccountSettingsPage = () => {
  const { t, i18n } = useTranslation();
  const [searchParams] = useSearchParams();
  // Favoritos/links antigos Conta?api_messages=* → hub de integração (Tokens).
  const legacyApiMessages = searchParams.get("api_messages");
  const helpHref = helpUrlForRoute(
    "/settings/account",
    i18n.language || "pt-BR"
  );

  const session = getLoggedinUser();
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [logoUrl, setLogoUrl] = useState<string | null>(
    session?.tenant?.logo_url || null
  );
  const [avatarUrl, setAvatarUrl] = useState<string | null>(
    session?.user?.avatar_url || null
  );
  const [trackingHostname, setTrackingHostname] = useState<string | null>(
    session?.tenant?.tracking_hostname || null
  );
  const [tenantDisplayName, setTenantDisplayName] = useState<string>(
    session?.tenant?.name || ""
  );
  const [logoBusy, setLogoBusy] = useState(false);
  const [avatarBusy, setAvatarBusy] = useState(false);
  // Object URL do arquivo escolhido — crop antes do upload (logo ~3:1, avatar 1:1).
  const [logoCropSrc, setLogoCropSrc] = useState<string | null>(null);
  const [avatarCropSrc, setAvatarCropSrc] = useState<string | null>(null);
  const [shellFavorites, setShellFavorites] = useState<ShellFavorite[]>(() =>
    readShellFavorites()
  );
  const [initial, setInitial] = useState<AccountFormValues>({
    name: session?.user?.name || "",
    email: session?.user?.email || "",
    ...emptySecrets,
    locale: session?.user?.locale || i18n.language || "pt-BR",
    timezone: session?.tenant?.timezone || DEFAULT_TENANT_TIMEZONE,
    user_timezone: session?.user?.timezone || "",
  });

  // Brand Kit F2
  const [brandKit, setBrandKit] = useState<BrandKit | null>(null);
  const [brandKitLoading, setBrandKitLoading] = useState(false);
  const [brandKitSaving, setBrandKitSaving] = useState(false);
  const [brandKitError, setBrandKitError] = useState<string | null>(null);
  const [brandKitDocument, setBrandKitDocument] = useState("");
  const [brandKitLegalName, setBrandKitLegalName] = useState("");
  const [brandKitAddress, setBrandKitAddress] = useState("");
  const [brandKitPrimaryColor, setBrandKitPrimaryColor] = useState("");
  const [brandKitSecondaryColor, setBrandKitSecondaryColor] = useState("");
  const [brandKitFontFamily, setBrandKitFontFamily] = useState("");
  const [brandKitApplyDefault, setBrandKitApplyDefault] = useState(false);

  const [hygieneThreshold, setHygieneThreshold] = useState(20);
  const [hygieneCountApi, setHygieneCountApi] = useState(true);
  const [hygieneLoading, setHygieneLoading] = useState(false);
  const [hygieneSaving, setHygieneSaving] = useState(false);
  const [hygieneError, setHygieneError] = useState<string | null>(null);

  const loadProfile = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const me = await fetchMe();
      mergeTenantMeIntoSession(me);
      setInitial({
        name: me.user.name || "",
        email: me.user.email || "",
        ...emptySecrets,
        locale: me.user.locale || "pt-BR",
        timezone: me.tenant.timezone || DEFAULT_TENANT_TIMEZONE,
        user_timezone: me.user.timezone || "",
      });
      setLogoUrl(me.tenant.logo_url || null);
      setAvatarUrl(me.user.avatar_url || null);
      setTrackingHostname(me.tenant.tracking_hostname || null);
      setTenantDisplayName(me.tenant.name || "");
    } catch (e: any) {
      setLoadError(e?.message || t("settings.account.loadError"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  const loadHygieneSettings = useCallback(async () => {
    setHygieneLoading(true);
    setHygieneError(null);
    try {
      const res = await getInactiveContactsSummary({ days: 90 });
      if (res?.success && res.data) {
        setHygieneThreshold(res.data.dashboard_threshold_percent ?? 20);
        setHygieneCountApi(res.data.inactive_count_api_messages ?? true);
      }
    } catch (e: any) {
      setHygieneError(e?.message || t("settings.account.hygiene.loadError"));
    } finally {
      setHygieneLoading(false);
    }
  }, [t]);

  const saveHygieneSettings = async () => {
    setHygieneSaving(true);
    setHygieneError(null);
    try {
      const value = Math.max(5, Math.min(80, Math.round(Number(hygieneThreshold))));
      const res = await updateInactiveListSettings({
        dashboard_threshold_percent: value,
        inactive_count_api_messages: hygieneCountApi,
      });
      if (!res?.success) {
        setHygieneError(t("settings.account.hygiene.saveError"));
        return;
      }
      setHygieneThreshold(res.data.dashboard_threshold_percent);
      setHygieneCountApi(res.data.inactive_count_api_messages);
      toast.success(t("settings.account.hygiene.saveSuccess"), { autoClose: 3000 });
    } catch (e: any) {
      setHygieneError(e?.message || t("settings.account.hygiene.saveError"));
    } finally {
      setHygieneSaving(false);
    }
  };

  const loadBrandKit = useCallback(async () => {
    setBrandKitLoading(true);
    setBrandKitError(null);
    try {
      const kit = await fetchBrandKit();
      setBrandKit(kit);
      setBrandKitDocument(kit.document || "");
      setBrandKitLegalName(kit.brand_legal_name || "");
      setBrandKitAddress(kit.brand_address || "");
      setBrandKitPrimaryColor(kit.brand_primary_color || "");
      setBrandKitSecondaryColor(kit.brand_secondary_color || "");
      setBrandKitFontFamily(kit.brand_font_family || "");
      setBrandKitApplyDefault(kit.brand_kit_apply_default || false);
    } catch (e: any) {
      setBrandKitError(e?.message || t("settings.account.brandKit.loadError"));
    } finally {
      setBrandKitLoading(false);
    }
  }, [t]);

  const saveBrandKit = async () => {
    setBrandKitSaving(true);
    setBrandKitError(null);
    try {
      const { data, message } = await updateBrandKit({
        document: brandKitDocument.trim() || null,
        brand_legal_name: brandKitLegalName.trim() || null,
        brand_address: brandKitAddress.trim() || null,
        brand_primary_color: brandKitPrimaryColor.trim() || null,
        brand_secondary_color: brandKitSecondaryColor.trim() || null,
        brand_font_family: brandKitFontFamily.trim() || null,
        brand_kit_apply_default: brandKitApplyDefault,
      });
      setBrandKit(data);
      toast.success(message || t("settings.account.brandKit.saveSuccess"), {
        autoClose: 3000,
      });
    } catch (e: any) {
      const msg = e?.message || t("settings.account.brandKit.saveError");
      setBrandKitError(msg);
      toast.error(msg, { autoClose: 5000 });
    } finally {
      setBrandKitSaving(false);
    }
  };

  const onLogoSelected = async (file: File | null) => {
    if (!file || logoBusy) {
      return;
    }
    setLogoBusy(true);
    try {
      const result = await uploadBrandLogo(file);
      setLogoUrl(result.logo_url);
      toast.success(result.message || t("settings.account.logo.successUpload"), {
        autoClose: 4000,
      });
      if (!trackingHostname) {
        toast.info(t("settings.account.logo.trackingNeeded"), {
          autoClose: 7000,
        });
      }
      const me = await fetchMe();
      mergeTenantMeIntoSession(me);
      setTrackingHostname(me.tenant.tracking_hostname || null);
      setTenantDisplayName(me.tenant.name || "");
    } catch (e: any) {
      toast.error(e?.message || t("settings.account.logo.error"), {
        autoClose: 5000,
      });
    } finally {
      setLogoBusy(false);
    }
  };

  const onLogoRemove = async () => {
    if (logoBusy) {
      return;
    }
    setLogoBusy(true);
    try {
      const result = await deleteBrandLogo();
      setLogoUrl(null);
      toast.success(result.message || t("settings.account.logo.successRemove"), {
        autoClose: 4000,
      });
      const me = await fetchMe();
      mergeTenantMeIntoSession(me);
    } catch (e: any) {
      toast.error(e?.message || t("settings.account.logo.error"), {
        autoClose: 5000,
      });
    } finally {
      setLogoBusy(false);
    }
  };

  const clearLogoCrop = () => {
    if (logoCropSrc) {
      URL.revokeObjectURL(logoCropSrc);
    }
    setLogoCropSrc(null);
  };

  const clearAvatarCrop = () => {
    if (avatarCropSrc) {
      URL.revokeObjectURL(avatarCropSrc);
    }
    setAvatarCropSrc(null);
  };

  const onAvatarSelected = async (file: File | null) => {
    if (!file || avatarBusy) {
      return;
    }
    setAvatarBusy(true);
    try {
      const result = await uploadUserAvatar(file);
      setAvatarUrl(result.avatar_url);
      toast.success(
        result.message || t("settings.account.avatar.successUpload"),
        { autoClose: 4000 }
      );
      const me = await fetchMe();
      mergeTenantMeIntoSession(me);
      setAvatarUrl(me.user.avatar_url || null);
    } catch (e: any) {
      toast.error(e?.message || t("settings.account.avatar.error"), {
        autoClose: 5000,
      });
    } finally {
      setAvatarBusy(false);
    }
  };

  const onAvatarRemove = async () => {
    if (avatarBusy) {
      return;
    }
    setAvatarBusy(true);
    try {
      const result = await deleteUserAvatar();
      setAvatarUrl(null);
      toast.success(
        result.message || t("settings.account.avatar.successRemove"),
        { autoClose: 4000 }
      );
      const me = await fetchMe();
      mergeTenantMeIntoSession(me);
    } catch (e: any) {
      toast.error(e?.message || t("settings.account.avatar.error"), {
        autoClose: 5000,
      });
    } finally {
      setAvatarBusy(false);
    }
  };

  const toggleShellFavoriteItem = (item: ShellFavorite) => {
    const exists = isFavoriteId(item.id, shellFavorites);
    const next = exists
      ? shellFavorites.filter((f) => f.id !== item.id)
      : [...shellFavorites, item];
    writeShellFavorites(next);
    setShellFavorites(next);
    toast.success(t("settings.account.favorites.updated"), { autoClose: 2500 });
  };

  useEffect(() => {
    void loadProfile();
    void loadBrandKit();
    void loadHygieneSettings();
  }, [loadProfile, loadBrandKit, loadHygieneSettings]);

  // Deep-link Conta#avatar / #favorites (e id completo account-*).
  useEffect(() => {
    if (loading) {
      return;
    }
    const raw = (window.location.hash || "").replace(/^#/, "");
    if (!raw) {
      return;
    }
    const id =
      raw === "avatar"
        ? "account-avatar"
        : raw === "favorites"
          ? "account-favorites"
          : raw;
    const el = document.getElementById(id);
    if (el) {
      window.setTimeout(() => {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 80);
    }
  }, [loading]);

  const timezoneOptions = useMemo(() => {
    const set = new Set<string>([...ACCOUNT_TIMEZONE_OPTIONS]);
    if (initial.timezone) {
      set.add(initial.timezone);
    }
    if (initial.user_timezone) {
      set.add(initial.user_timezone);
    }
    return Array.from(set);
  }, [initial.timezone, initial.user_timezone]);

  const validation = useFormik<AccountFormValues>({
    enableReinitialize: true,
    initialValues: initial,
    validationSchema: Yup.object({
      name: Yup.string()
        .trim()
        .required(t("settings.account.validation.nameRequired")),
      email: Yup.string()
        .trim()
        .email(t("settings.account.validation.emailInvalid"))
        .required(t("settings.account.validation.emailRequired")),
      current_password: Yup.string().when("password", {
        is: (v: string | undefined) => !!v,
        then: (schema) =>
          schema.required(t("settings.account.validation.currentRequired")),
      }),
      password: Yup.string()
        .transform((v) => (v === "" ? undefined : v))
        .min(8, t("settings.account.validation.passwordMin")),
      password_confirmation: Yup.string().when("password", {
        is: (v: string | undefined) => !!v,
        then: (schema) =>
          schema
            .required(t("settings.account.validation.passwordMismatch"))
            .oneOf(
              [Yup.ref("password")],
              t("settings.account.validation.passwordMismatch")
            ),
      }),
      locale: Yup.string()
        .oneOf([...LOCALES], t("settings.account.validation.localeInvalid"))
        .required(t("settings.account.validation.localeRequired")),
      timezone: Yup.string().required(
        t("settings.account.validation.timezoneRequired")
      ),
    }),
    onSubmit: async (values) => {
      setSaving(true);
      setFormError(null);
      const emailChanged =
        values.email.trim().toLowerCase() !== initial.email.toLowerCase();
      try {
        const payload: UpdateProfilePayload & {
          current_password?: string;
        } = {
          name: values.name.trim(),
          email: values.email.trim().toLowerCase(),
          locale: values.locale,
          timezone: values.timezone,
          // null limpa fuso pessoal e volta a herdar a conta.
          user_timezone: values.user_timezone.trim() || null,
        };
        if (values.password) {
          payload.current_password = values.current_password;
          payload.password = values.password;
          payload.password_confirmation = values.password_confirmation;
        }

        const { data, message } = await updateProfile(payload);
        mergeTenantMeIntoSession(data);

        setInitial({
          name: data.user.name || "",
          email: data.user.email || "",
          ...emptySecrets,
          locale: data.user.locale || "pt-BR",
          timezone: data.tenant.timezone || DEFAULT_TENANT_TIMEZONE,
          user_timezone: data.user.timezone || "",
        });
        validation.resetForm({
          values: {
            name: data.user.name || "",
            email: data.user.email || "",
            ...emptySecrets,
            locale: data.user.locale || "pt-BR",
            timezone: data.tenant.timezone || DEFAULT_TENANT_TIMEZONE,
            user_timezone: data.user.timezone || "",
          },
        });
        setAvatarUrl(data.user.avatar_url || null);

        const nextLocale = data.user.locale || values.locale;
        if (nextLocale && nextLocale !== i18n.language) {
          localStorage.setItem("I18N_LANGUAGE", nextLocale);
          await i18n.changeLanguage(nextLocale);
        }

        toast.success(message || t("settings.account.success"), {
          autoClose: 3000,
        });
        if (emailChanged) {
          toast.info(t("settings.account.emailChangedNotice"), {
            autoClose: 6000,
          });
        }
      } catch (e: any) {
        const msg = e?.message || t("settings.account.error.generic");
        setFormError(msg);
        toast.error(msg, { autoClose: 5000 });
      } finally {
        setSaving(false);
      }
    },
  });

  const isDirty = useMemo(() => {
    const v = validation.values;
    return (
      v.name.trim() !== initial.name.trim() ||
      v.email.trim().toLowerCase() !== initial.email.toLowerCase() ||
      v.locale !== initial.locale ||
      v.timezone !== initial.timezone ||
      v.user_timezone !== initial.user_timezone ||
      !!v.password ||
      !!v.current_password ||
      !!v.password_confirmation
    );
  }, [validation.values, initial]);

  // Guarda SPA via contexto (menu) + beforeunload no provider — evita duplicar listener.
  const { setDirty } = useUnsavedChanges();
  useEffect(() => {
    setDirty(isDirty && !saving);
    return () => setDirty(false);
  }, [isDirty, saving, setDirty]);

  const emailDirty =
    validation.values.email.trim().toLowerCase() !==
    initial.email.toLowerCase();
  const timezoneDirty = validation.values.timezone !== initial.timezone;

  const avatarInitials = useMemo(() => {
    const parts = (validation.values.name || "").trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) {
      return "?";
    }
    if (parts.length === 1) {
      return parts[0].slice(0, 2).toUpperCase();
    }
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }, [validation.values.name]);

  document.title = t("settings.account.documentTitle");

  if (legacyApiMessages) {
    return (
      <Navigate
        to={`/settings/api-tokens?api_messages=${encodeURIComponent(legacyApiMessages)}`}
        replace
      />
    );
  }

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb
          title={t("settings.account.pageTitle")}
          actions={
            <PageHelpLink
              href={helpHref}
              label={t("settings.account.help")}
            />
          }
        />
        <ToastContainer closeButton={false} limit={3} />

        {loading ? (
          <div className="text-center py-5" role="status">
            <Spinner className="me-2" />
            {t("settings.account.loading")}
          </div>
        ) : null}

        {!loading && loadError ? (
          <Alert color="danger" role="alert">
            {loadError}
            <div className="mt-2">
              <Button
                color="soft-danger"
                size="sm"
                onClick={() => void loadProfile()}
              >
                {t("settings.account.retry")}
              </Button>
            </div>
          </Alert>
        ) : null}

        {!loading && !loadError ? (
          <Row className="justify-content-center">
            <Col lg={8} xl={7}>
              <p className="text-muted mb-3">
                {t("settings.account.subtitle")}{" "}
                <a href={helpHref} target="_blank" rel="noopener noreferrer">
                  {t("settings.account.help")}
                </a>
              </p>

              {formError ? (
                <Alert color="danger" role="alert">
                  {formError}
                </Alert>
              ) : null}

              <Form
                onSubmit={(e) => {
                  e.preventDefault();
                  validation.handleSubmit();
                  return false;
                }}
              >
                <Card className="mb-3">
                  <CardHeader>
                    <h5 className="card-title mb-0">
                      {t("settings.account.section.profile")}
                    </h5>
                  </CardHeader>
                  <CardBody>
                    <div className="mb-3">
                      <Label htmlFor="account-name">
                        {t("settings.account.name")}
                      </Label>
                      <Input
                        id="account-name"
                        name="name"
                        type="text"
                        autoComplete="name"
                        value={validation.values.name}
                        onChange={validation.handleChange}
                        onBlur={validation.handleBlur}
                        invalid={
                          !!(validation.touched.name && validation.errors.name)
                        }
                      />
                      {validation.touched.name && validation.errors.name ? (
                        <FormFeedback type="invalid">
                          {String(validation.errors.name)}
                        </FormFeedback>
                      ) : null}
                    </div>
                    <div className="mb-0">
                      <Label htmlFor="account-email">
                        {t("settings.account.email")}
                      </Label>
                      <Input
                        id="account-email"
                        name="email"
                        type="email"
                        autoComplete="email"
                        value={validation.values.email}
                        onChange={validation.handleChange}
                        onBlur={validation.handleBlur}
                        invalid={
                          !!(
                            validation.touched.email && validation.errors.email
                          )
                        }
                      />
                      {validation.touched.email && validation.errors.email ? (
                        <FormFeedback type="invalid">
                          {String(validation.errors.email)}
                        </FormFeedback>
                      ) : null}
                      <div className="form-text">
                        {t("settings.account.emailHint")}
                      </div>
                      {emailDirty ? (
                        <Alert color="warning" className="mt-2 mb-0 py-2">
                          {t("settings.account.emailChangeWarning")}
                        </Alert>
                      ) : null}
                    </div>
                  </CardBody>
                </Card>

                <Card className="mb-3" id="account-avatar">
                  <CardHeader>
                    <h5 className="card-title mb-0">
                      {t("settings.account.avatar.section")}
                    </h5>
                  </CardHeader>
                  <CardBody>
                    <p className="text-muted small mb-3">
                      {t("settings.account.avatar.hint")}
                    </p>
                    <div className="d-flex flex-wrap gap-3 align-items-center mb-3">
                      {avatarUrl ? (
                        <img
                          src={avatarUrl}
                          alt=""
                          className="rounded-circle border"
                          style={{ width: 72, height: 72, objectFit: "cover" }}
                          onError={() => {
                            setAvatarUrl(null);
                            toast.warning(t("settings.account.imageLoadFailed"), {
                              autoClose: 6000,
                            });
                          }}
                        />
                      ) : (
                        <div
                          className="rounded-circle border d-flex align-items-center justify-content-center bg-light text-muted fw-semibold"
                          style={{ width: 72, height: 72 }}
                          aria-hidden
                        >
                          {avatarInitials}
                        </div>
                      )}
                      <div className="d-flex flex-wrap gap-2 align-items-center">
                        <Label
                          htmlFor="account-avatar-file"
                          className={`btn btn-soft-primary mb-0 ${
                            avatarBusy ? "disabled" : ""
                          }`}
                        >
                          {avatarBusy ? (
                            <Spinner size="sm" className="me-1" />
                          ) : null}
                          {t("settings.account.avatar.upload")}
                        </Label>
                        <Input
                          id="account-avatar-file"
                          type="file"
                          accept="image/png,image/jpeg,image/webp"
                          className="d-none"
                          disabled={avatarBusy}
                          onChange={(e) => {
                            const file = e.target.files?.[0] || null;
                            e.target.value = "";
                            if (!file) {
                              return;
                            }
                            setAvatarCropSrc(URL.createObjectURL(file));
                          }}
                        />
                        {avatarUrl ? (
                          <Button
                            color="soft-danger"
                            type="button"
                            disabled={avatarBusy}
                            onClick={() => void onAvatarRemove()}
                          >
                            {t("settings.account.avatar.remove")}
                          </Button>
                        ) : null}
                      </div>
                    </div>
                  </CardBody>
                </Card>

                <AccountMfaSection />
                <AccountGoogleSection />

                <Card className="mb-3">
                  <CardHeader>
                    <h5 className="card-title mb-0">
                      {t("settings.account.section.password")}
                    </h5>
                  </CardHeader>
                  <CardBody>
                    <p className="text-muted small">
                      {t("settings.account.passwordHint")}
                    </p>
                    <div className="mb-3">
                      <Label htmlFor="account-current-password">
                        {t("settings.account.currentPassword")}
                      </Label>
                      <Input
                        id="account-current-password"
                        name="current_password"
                        type="password"
                        autoComplete="current-password"
                        value={validation.values.current_password}
                        onChange={validation.handleChange}
                        onBlur={validation.handleBlur}
                        invalid={
                          !!(
                            validation.touched.current_password &&
                            validation.errors.current_password
                          )
                        }
                      />
                      {validation.touched.current_password &&
                      validation.errors.current_password ? (
                        <FormFeedback type="invalid">
                          {String(validation.errors.current_password)}
                        </FormFeedback>
                      ) : null}
                      <div className="form-text">
                        {t("settings.account.currentPasswordHint")}
                      </div>
                    </div>
                    <div className="mb-3">
                      <Label htmlFor="account-password">
                        {t("settings.account.newPassword")}
                      </Label>
                      <Input
                        id="account-password"
                        name="password"
                        type="password"
                        autoComplete="new-password"
                        value={validation.values.password}
                        onChange={validation.handleChange}
                        onBlur={validation.handleBlur}
                        invalid={
                          !!(
                            validation.touched.password &&
                            validation.errors.password
                          )
                        }
                      />
                      {validation.touched.password &&
                      validation.errors.password ? (
                        <FormFeedback type="invalid">
                          {String(validation.errors.password)}
                        </FormFeedback>
                      ) : null}
                    </div>
                    <div className="mb-0">
                      <Label htmlFor="account-password-confirm">
                        {t("settings.account.confirmPassword")}
                      </Label>
                      <Input
                        id="account-password-confirm"
                        name="password_confirmation"
                        type="password"
                        autoComplete="new-password"
                        value={validation.values.password_confirmation}
                        onChange={validation.handleChange}
                        onBlur={validation.handleBlur}
                        invalid={
                          !!(
                            validation.touched.password_confirmation &&
                            validation.errors.password_confirmation
                          )
                        }
                      />
                      {validation.touched.password_confirmation &&
                      validation.errors.password_confirmation ? (
                        <FormFeedback type="invalid">
                          {String(validation.errors.password_confirmation)}
                        </FormFeedback>
                      ) : null}
                    </div>
                  </CardBody>
                </Card>

                <Card className="mb-3">
                  <CardHeader>
                    <h5 className="card-title mb-0">
                      {t("settings.account.section.brand")}
                    </h5>
                  </CardHeader>
                  <CardBody>
                    <p className="text-muted small mb-3">
                      {t("settings.account.logo.hint")}
                    </p>
                    {!trackingHostname ? (
                      <Alert color="warning" className="py-2 small">
                        {t("settings.account.logo.trackingNeeded")}{" "}
                        <a href="/settings/sending">
                          {t("settings.account.logo.trackingCta")}
                        </a>
                      </Alert>
                    ) : null}

                    {/* Mini preview — como o destinatário vê a página pública. */}
                    <div
                      className="mb-3 p-3 border rounded"
                      style={{ background: "#f0f4f9" }}
                    >
                      <div className="small text-muted mb-2">
                        {t("settings.account.logo.previewTitle")}
                      </div>
                      <div
                        className="bg-white border rounded p-3 mx-auto"
                        style={{ maxWidth: 320 }}
                      >
                        {logoUrl ? (
                          <div className="text-center mb-3">
                            <img
                              src={logoUrl}
                              alt={t("settings.account.logo.previewAlt")}
                              style={{ maxHeight: 48, maxWidth: 200 }}
                              onError={() => {
                                setLogoUrl(null);
                                toast.warning(
                                  t("settings.account.imageLoadFailed"),
                                  { autoClose: 6000 }
                                );
                              }}
                            />
                          </div>
                        ) : (
                          <div
                            className="text-center text-muted small mb-3 py-2 border border-dashed rounded"
                            style={{ borderStyle: "dashed" }}
                          >
                            {t("settings.account.logo.previewNoLogo")}
                          </div>
                        )}
                        <p className="text-center text-muted small mb-1">
                          {t("settings.account.logo.previewFrom", {
                            sender:
                              tenantDisplayName ||
                              t("public.unsubscribe.senderFallback"),
                          })}
                        </p>
                        <h6 className="text-center mb-2">
                          {t("public.unsubscribe.confirmTitle")}
                        </h6>
                        <p className="text-muted text-center small mb-0">
                          {t("settings.account.logo.previewHint")}
                        </p>
                      </div>
                      {trackingHostname ? (
                        <div className="form-text mt-2 mb-0">
                          {t("settings.account.logo.previewHost", {
                            host: trackingHostname,
                          })}
                        </div>
                      ) : null}
                    </div>

                    <div className="d-flex flex-wrap gap-2 align-items-center">
                      <Label
                        htmlFor="account-logo-file"
                        className={`btn btn-soft-primary mb-0 ${
                          logoBusy ? "disabled" : ""
                        }`}
                      >
                        {logoBusy ? (
                          <Spinner size="sm" className="me-1" />
                        ) : null}
                        {t("settings.account.logo.upload")}
                      </Label>
                      <Input
                        id="account-logo-file"
                        type="file"
                        accept="image/png,image/jpeg,image/webp,image/gif"
                        className="d-none"
                        disabled={logoBusy}
                        onChange={(e) => {
                          const file = e.target.files?.[0] || null;
                          e.target.value = "";
                          if (!file) {
                            return;
                          }
                          // Crop antes do upload — evita logo “esticada” no unsub.
                          setLogoCropSrc(URL.createObjectURL(file));
                        }}
                      />
                      {logoUrl ? (
                        <Button
                          color="soft-danger"
                          type="button"
                          disabled={logoBusy}
                          onClick={() => void onLogoRemove()}
                        >
                          {t("settings.account.logo.remove")}
                        </Button>
                      ) : null}
                    </div>
                  </CardBody>
                </Card>

                <Card className="mb-3">
                  <CardHeader>
                    <h5 className="card-title mb-0">
                      {t("settings.account.section.brandKit")}
                    </h5>
                  </CardHeader>
                  <CardBody>
                    <p className="text-muted small mb-3">
                      {t("settings.account.brandKit.hint")}
                    </p>

                    {brandKitLoading && (
                      <div className="text-center py-3">
                        <Spinner size="sm" className="me-2" />
                        {t("settings.account.brandKit.loading")}
                      </div>
                    )}

                    {brandKitError && (
                      <Alert color="danger" className="py-2">
                        {brandKitError}
                      </Alert>
                    )}

                    {!brandKitLoading && (
                      <>
                        <div className="mb-3">
                          <Label htmlFor="brand-kit-document">
                            {t("settings.account.brandKit.document")}
                          </Label>
                          <Input
                            id="brand-kit-document"
                            type="text"
                            value={brandKitDocument}
                            onChange={(e) => setBrandKitDocument(e.target.value)}
                            placeholder={t(
                              "settings.account.brandKit.documentPlaceholder"
                            )}
                          />
                          <div className="form-text">
                            {t("settings.account.brandKit.documentHint")}
                          </div>
                        </div>

                        <div className="mb-3">
                          <Label htmlFor="brand-kit-legal-name">
                            {t("settings.account.brandKit.legalName")}
                          </Label>
                          <Input
                            id="brand-kit-legal-name"
                            type="text"
                            value={brandKitLegalName}
                            onChange={(e) => setBrandKitLegalName(e.target.value)}
                            placeholder={t(
                              "settings.account.brandKit.legalNamePlaceholder"
                            )}
                          />
                        </div>

                        <div className="mb-3">
                          <Label htmlFor="brand-kit-address">
                            {t("settings.account.brandKit.address")}
                          </Label>
                          <Input
                            id="brand-kit-address"
                            type="textarea"
                            rows={2}
                            value={brandKitAddress}
                            onChange={(e) => setBrandKitAddress(e.target.value)}
                            placeholder={t(
                              "settings.account.brandKit.addressPlaceholder"
                            )}
                          />
                        </div>

                        <Row>
                          <Col md={6}>
                            <div className="mb-3">
                              <Label htmlFor="brand-kit-primary-color">
                                {t("settings.account.brandKit.primaryColor")}
                              </Label>
                              <Input
                                id="brand-kit-primary-color"
                                type="color"
                                value={brandKitPrimaryColor || "#0d6efd"}
                                onChange={(e) =>
                                  setBrandKitPrimaryColor(e.target.value)
                                }
                              />
                            </div>
                          </Col>
                          <Col md={6}>
                            <div className="mb-3">
                              <Label htmlFor="brand-kit-secondary-color">
                                {t("settings.account.brandKit.secondaryColor")}
                              </Label>
                              <Input
                                id="brand-kit-secondary-color"
                                type="color"
                                value={brandKitSecondaryColor || "#6c757d"}
                                onChange={(e) =>
                                  setBrandKitSecondaryColor(e.target.value)
                                }
                              />
                            </div>
                          </Col>
                        </Row>

                        <div className="mb-3">
                          <Label htmlFor="brand-kit-font">
                            {t("settings.account.brandKit.fontFamily")}
                          </Label>
                          <Input
                            id="brand-kit-font"
                            type="select"
                            value={brandKitFontFamily}
                            onChange={(e) => setBrandKitFontFamily(e.target.value)}
                          >
                            <option value="">
                              {t("settings.account.brandKit.fontDefault")}
                            </option>
                            <option value="Arial, sans-serif">Arial</option>
                            <option value="Georgia, serif">Georgia</option>
                            <option value="'Times New Roman', serif">
                              Times New Roman
                            </option>
                            <option value="Verdana, sans-serif">Verdana</option>
                            <option value="'Courier New', monospace">
                              Courier New
                            </option>
                            <option value="'Trebuchet MS', sans-serif">
                              Trebuchet MS
                            </option>
                          </Input>
                        </div>

                        <div className="mb-3">
                          <div className="form-check">
                            <Input
                              id="brand-kit-apply-default"
                              type="checkbox"
                              className="form-check-input"
                              checked={brandKitApplyDefault}
                              onChange={(e) =>
                                setBrandKitApplyDefault(e.target.checked)
                              }
                            />
                            <Label
                              htmlFor="brand-kit-apply-default"
                              className="form-check-label"
                            >
                              {t("settings.account.brandKit.applyDefault")}
                            </Label>
                          </div>
                          <div className="form-text">
                            {t("settings.account.brandKit.applyDefaultHint")}
                          </div>
                        </div>

                        {brandKit?.footer_preview_html && (
                          <div className="mb-3">
                            <Label>
                              {t("settings.account.brandKit.footerPreview")}
                            </Label>
                            <div
                              className="border rounded p-3"
                              style={{ background: "#f8f9fa" }}
                              dangerouslySetInnerHTML={{
                                __html: brandKit.footer_preview_html,
                              }}
                            />
                          </div>
                        )}

                        <Button
                          color="primary"
                          type="button"
                          disabled={brandKitSaving}
                          onClick={() => void saveBrandKit()}
                        >
                          {brandKitSaving ? (
                            <>
                              <Spinner size="sm" className="me-2">
                                …
                              </Spinner>
                              {t("settings.account.brandKit.saving")}
                            </>
                          ) : (
                            t("settings.account.brandKit.save")
                          )}
                        </Button>
                      </>
                    )}
                  </CardBody>
                </Card>

                <Card className="mb-3">
                  <CardHeader>
                    <h5 className="card-title mb-0">
                      {t("settings.account.section.preferences")}
                    </h5>
                  </CardHeader>
                  <CardBody>
                    <div className="mb-3">
                      <Label htmlFor="account-locale">
                        {t("settings.account.locale")}
                      </Label>
                      <Input
                        id="account-locale"
                        name="locale"
                        type="select"
                        value={validation.values.locale}
                        onChange={validation.handleChange}
                        onBlur={validation.handleBlur}
                        invalid={
                          !!(
                            validation.touched.locale &&
                            validation.errors.locale
                          )
                        }
                      >
                        <option value="pt-BR">
                          {t("settings.account.locale.ptBR")}
                        </option>
                        <option value="en">
                          {t("settings.account.locale.en")}
                        </option>
                        <option value="es">
                          {t("settings.account.locale.es")}
                        </option>
                      </Input>
                      {validation.touched.locale &&
                      validation.errors.locale ? (
                        <FormFeedback type="invalid">
                          {String(validation.errors.locale)}
                        </FormFeedback>
                      ) : null}
                    </div>
                    <div className="mb-3">
                      <Label htmlFor="account-timezone">
                        {t("settings.account.timezone")}
                      </Label>
                      <Input
                        id="account-timezone"
                        name="timezone"
                        type="select"
                        value={validation.values.timezone}
                        onChange={validation.handleChange}
                        onBlur={validation.handleBlur}
                        invalid={
                          !!(
                            validation.touched.timezone &&
                            validation.errors.timezone
                          )
                        }
                      >
                        {timezoneOptions.map((tz) => (
                          <option key={tz} value={tz}>
                            {tenantTimezoneDisplayLabel(tz, t)}
                          </option>
                        ))}
                      </Input>
                      {validation.touched.timezone &&
                      validation.errors.timezone ? (
                        <FormFeedback type="invalid">
                          {String(validation.errors.timezone)}
                        </FormFeedback>
                      ) : null}
                      <div className="form-text">
                        {t("settings.account.timezoneHint")}
                      </div>
                      {timezoneDirty ? (
                        <Alert color="info" className="mt-2 mb-0 py-2">
                          {t("settings.account.timezoneChangeNotice", {
                            timezone: tenantTimezoneDisplayLabel(
                              validation.values.timezone,
                              t
                            ),
                          })}
                        </Alert>
                      ) : null}
                    </div>
                    <div className="mb-3 mt-3">
                      <Label htmlFor="account-user-timezone">
                        {t("settings.account.userTimezone")}
                      </Label>
                      <Input
                        id="account-user-timezone"
                        name="user_timezone"
                        type="select"
                        value={validation.values.user_timezone}
                        onChange={validation.handleChange}
                        onBlur={validation.handleBlur}
                      >
                        <option value="">
                          {t("settings.account.userTimezoneInherit")}
                        </option>
                        {timezoneOptions.map((tz) => (
                          <option key={tz} value={tz}>
                            {tenantTimezoneDisplayLabel(tz, t)}
                          </option>
                        ))}
                      </Input>
                      <div className="form-text">
                        {t("settings.account.userTimezoneHint")}
                      </div>
                    </div>
                    <div className="mb-0" id="account-favorites">
                      <Label className="d-block">
                        {t("settings.account.favorites.section")}
                      </Label>
                      <p className="text-muted small mb-2">
                        {t("settings.account.favorites.hint")}
                      </p>
                      {PINNABLE_SHELL_ITEMS.map((item) => (
                        <div className="form-check" key={item.id}>
                          <Input
                            id={`shell-fav-${item.id}`}
                            type="checkbox"
                            className="form-check-input"
                            checked={isFavoriteId(item.id, shellFavorites)}
                            onChange={() => toggleShellFavoriteItem(item)}
                          />
                          <Label
                            htmlFor={`shell-fav-${item.id}`}
                            className="form-check-label"
                          >
                            {t(item.label)}
                          </Label>
                        </div>
                      ))}
                    </div>
                  </CardBody>
                </Card>

                <Card className="mb-3">
                  <CardHeader>
                    <h5 className="card-title mb-0">
                      {t("settings.account.section.hygiene")}
                    </h5>
                  </CardHeader>
                  <CardBody>
                    <p className="text-muted small mb-3">
                      {t("settings.account.hygiene.hint")}{" "}
                      <a href="/contacts/inactive">{t("settings.account.hygiene.link")}</a>
                    </p>
                    {hygieneLoading ? (
                      <div className="text-center py-2">
                        <Spinner size="sm" className="me-2" />
                        {t("settings.account.hygiene.loading")}
                      </div>
                    ) : null}
                    {hygieneError ? (
                      <Alert color="danger" className="py-2">
                        {hygieneError}
                      </Alert>
                    ) : null}
                    {!hygieneLoading ? (
                      <>
                        <div className="mb-3">
                          <Label htmlFor="account-hygiene-threshold">
                            {t("settings.account.hygiene.thresholdLabel")}
                          </Label>
                          <Input
                            id="account-hygiene-threshold"
                            type="number"
                            min={5}
                            max={80}
                            style={{ maxWidth: 120 }}
                            value={hygieneThreshold}
                            disabled={hygieneSaving}
                            onChange={(e) =>
                              setHygieneThreshold(Number(e.target.value))
                            }
                          />
                          <div className="form-text">
                            {t("settings.account.hygiene.thresholdHint")}
                          </div>
                        </div>
                        <div className="mb-3">
                          <div className="form-check">
                            <Input
                              id="account-hygiene-count-api"
                              type="checkbox"
                              className="form-check-input"
                              checked={hygieneCountApi}
                              disabled={hygieneSaving}
                              onChange={(e) => setHygieneCountApi(e.target.checked)}
                            />
                            <Label
                              htmlFor="account-hygiene-count-api"
                              className="form-check-label"
                            >
                              {t("settings.account.hygiene.countApiLabel")}
                            </Label>
                          </div>
                          <div className="form-text">
                            {t("settings.account.hygiene.countApiHint")}
                          </div>
                        </div>
                        <Button
                          color="primary"
                          type="button"
                          disabled={hygieneSaving}
                          onClick={() => void saveHygieneSettings()}
                        >
                          {hygieneSaving ? (
                            <>
                              <Spinner size="sm" className="me-2" />
                              {t("settings.account.hygiene.saving")}
                            </>
                          ) : (
                            t("settings.account.hygiene.save")
                          )}
                        </Button>
                      </>
                    ) : null}
                  </CardBody>
                </Card>

                <Button color="primary" type="submit" disabled={saving}>
                  {saving ? (
                    <>
                      <Spinner size="sm" className="me-2">
                        …
                      </Spinner>
                      {t("settings.account.saving")}
                    </>
                  ) : (
                    t("settings.account.submit")
                  )}
                </Button>
              </Form>
            </Col>
          </Row>
        ) : null}
      </Container>

      <ImageCropModal
        isOpen={!!logoCropSrc}
        imageSrc={logoCropSrc}
        aspect={3}
        titleKey="settings.account.crop.titleLogo"
        fileName="brand-logo.png"
        onCancel={clearLogoCrop}
        onConfirm={async (file) => {
          clearLogoCrop();
          await onLogoSelected(file);
        }}
      />
      <ImageCropModal
        isOpen={!!avatarCropSrc}
        imageSrc={avatarCropSrc}
        aspect={1}
        cropShape="round"
        titleKey="settings.account.crop.titleAvatar"
        fileName="avatar.png"
        onCancel={clearAvatarCrop}
        onConfirm={async (file) => {
          clearAvatarCrop();
          await onAvatarSelected(file);
        }}
      />
    </div>
  );
};

export default AccountSettingsPage;
